import { useEffect, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

/**
 * Return type for useMicPermission hook
 */
export interface UseMicPermissionReturnIProps {
  hasPermission: boolean | null
  isChecking: boolean
  requestPermission: () => Promise<void>
}

/**
 * Hook to check and request microphone permissions
 * Returns null initially while checking, then boolean once determined
 */
export const useMicPermission = (): UseMicPermissionReturnIProps => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null

    const checkPermission = async () => {
      try {
        setIsChecking(true)

        // Check if the Permissions API is available
        if (!navigator.permissions) {
          // Fallback: assume permission is granted if API not available
          setHasPermission(true)
          setIsChecking(false)
          return
        }

        // Query microphone permission status
        permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })

        setHasPermission(permissionStatus.state !== 'denied')

        // Listen for permission changes
        permissionStatus.onchange = () => {
          setHasPermission(permissionStatus!.state !== 'denied')
        }
      } catch (err) {
        // Assume permission is granted if we can't check
        Sentry.captureException(err)
        setHasPermission(true)
      } finally {
        setIsChecking(false)
      }
    }

    checkPermission()

    // Cleanup: remove permission change listener
    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [])

  const requestPermission = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Stop all tracks immediately after getting permission
      stream.getTracks().forEach((track) => track.stop())
      setHasPermission(true)
    } catch (err) {
      Sentry.captureException(err)
      setHasPermission(false)
    }
  }

  return {
    hasPermission,
    isChecking,
    requestPermission,
  }
}
